import React, { useState, useCallback } from 'react';
import {
  LOAN_TYPES, PROPERTY_TYPES, STATES, ENTITY_TYPES, EXIT_STRATEGIES,
  LOAN_TERMS, PPP_OPTIONS, FUNDS_SOURCES, FUNDS_STATUS,
  BANKRUPTCY_TYPES, BANKRUPTCY_STATUS, OCCUPANCY_STATUS,
  getVisiblePropertyFields, isPropertyFieldRequired,
  getLoanFieldSpec, showsRehabToggle, isIncomeProducingLoan,
  getAvailablePurposes,
} from './loanConfig';

const REHAB_FINANCING = ['Lender funds rehab', 'Borrower self-funds rehab'];

const css = {
  page: { minHeight: '100vh', background: 'var(--offwhite)', paddingBottom: '60px' },
  header: { background: 'var(--primary)', padding: '0', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 12px rgba(13,53,101,0.18)' },
  headerInner: { maxWidth: '860px', margin: '0 auto', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerLeft: { display: 'flex', flexDirection: 'column' },
  logoText: { color: '#fff', fontFamily: 'var(--font-serif)', fontSize: '20px', letterSpacing: '0.02em', lineHeight: 1.2 },
  headerSubtitle: { color: 'var(--accent)', fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: '2px' },
  accentRule: { height: '3px', background: 'var(--accent)' },
  body: { maxWidth: '860px', margin: '0 auto', padding: '36px 24px' },
  sectionCard: { background: '#fff', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', marginBottom: '24px', overflow: 'hidden' },
  sectionHeader: (expanded) => ({ background: 'var(--mid)', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none', borderBottom: expanded ? '2px solid var(--accent)' : 'none' }),
  sectionNum: { background: 'var(--accent)', color: 'var(--dark)', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700, flexShrink: 0 },
  sectionTitle: { color: '#fff', fontWeight: 600, fontSize: '14px', letterSpacing: '0.04em', flex: 1 },
  chevron: (expanded) => ({ color: '#fff', fontSize: '12px', transition: 'transform 0.2s', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', lineHeight: 1, display: 'inline-block', width: '14px', textAlign: 'center' }),
  sectionBody: { padding: '24px' },
  subSection: { borderTop: '1px solid #EAF1FA', paddingTop: '18px', marginTop: '18px' },
  subSectionTitle: { fontSize: '11px', fontWeight: 700, color: 'var(--mid)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '4px' },
  subSectionHint: { fontSize: '11px', color: '#8a9ab5', marginBottom: '14px', fontStyle: 'italic' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '18px' },
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '18px' },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '14px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '12px', fontWeight: 600, color: 'var(--mid)', letterSpacing: '0.06em', textTransform: 'uppercase' },
  required: { color: '#c0392b', marginLeft: '2px' },
  input: (err, focused) => ({ padding: '10px 14px', border: `1.5px solid ${err ? '#c0392b' : focused ? 'var(--primary)' : 'var(--rule)'}`, borderRadius: '8px', fontSize: '14px', color: 'var(--text)', background: '#fff', outline: 'none', transition: 'border-color 0.18s', width: '100%' }),
  select: (err, focused) => ({ padding: '10px 14px', border: `1.5px solid ${err ? '#c0392b' : focused ? 'var(--primary)' : 'var(--rule)'}`, borderRadius: '8px', fontSize: '14px', color: 'var(--text)', background: '#fff', outline: 'none', transition: 'border-color 0.18s', width: '100%', cursor: 'pointer', appearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%233A6BA8' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center', paddingRight: '36px' }),
  textarea: (err, focused) => ({ padding: '10px 14px', border: `1.5px solid ${err ? '#c0392b' : focused ? 'var(--primary)' : 'var(--rule)'}`, borderRadius: '8px', fontSize: '14px', color: 'var(--text)', background: '#fff', outline: 'none', transition: 'border-color 0.18s', width: '100%', minHeight: '70px', resize: 'vertical', fontFamily: 'var(--font-sans)', lineHeight: 1.5 }),
  errorMsg: { fontSize: '11px', color: '#c0392b', marginTop: '2px' },
  loanTypeGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' },
  loanTypeBtn: (selected) => ({ padding: '12px 16px', border: `2px solid ${selected ? 'var(--primary)' : 'var(--rule)'}`, borderRadius: '8px', background: selected ? 'var(--light)' : '#fff', color: selected ? 'var(--dark)' : 'var(--text)', fontWeight: selected ? 600 : 400, fontSize: '13px', cursor: 'pointer', transition: 'all 0.18s', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px' }),
  loanTypeDot: (selected) => ({ width: '10px', height: '10px', borderRadius: '50%', border: `2px solid ${selected ? 'var(--primary)' : 'var(--rule)'}`, background: selected ? 'var(--primary)' : 'transparent', flexShrink: 0, transition: 'all 0.18s' }),
  submitArea: { background: '#fff', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', padding: '28px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '24px', flexWrap: 'wrap' },
  submitNote: { fontSize: '13px', color: '#8a9ab5', maxWidth: '420px' },
  submitBtn: (loading) => ({ padding: '14px 36px', background: loading ? 'var(--mid)' : 'var(--primary)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '15px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', letterSpacing: '0.03em', transition: 'background 0.2s', display: 'flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap' }),
  errorBanner: { background: '#fef0f0', border: '1px solid #f5c6c6', borderRadius: '8px', padding: '14px 18px', marginBottom: '20px', fontSize: '14px', color: '#a02020' },
  secondBorrowerBox: { marginTop: '16px', padding: '16px', background: 'var(--stripe)', borderRadius: '8px', border: '1px solid var(--rule)' },
  secondBorrowerTitle: { fontSize: '12px', fontWeight: 700, color: 'var(--mid)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '14px' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--mid)', fontWeight: 600, marginTop: '18px' },
  creditChecks: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' },
  creditCheckBox: (checked) => ({ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', padding: '10px 12px', border: `1px solid ${checked ? 'var(--primary)' : 'var(--rule)'}`, borderRadius: '6px', background: checked ? 'var(--light)' : '#fff', cursor: 'pointer', userSelect: 'none' }),
  bankruptcyDetail: { marginTop: '12px', padding: '14px', background: '#FDF0DC', border: '1px solid #F5D49A', borderRadius: '6px' },
  bankruptcyDetailTitle: { fontSize: '11px', fontWeight: 700, color: '#7A4A08', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '10px' },
  numberBox: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: '1px solid var(--rule)', borderRadius: '6px', background: '#fff', fontSize: '13px' },
  numberInput: { width: '50px', padding: '4px 8px', border: '1px solid var(--rule)', borderRadius: '4px', fontSize: '13px', textAlign: 'center' },
  metricsBox: { background: '#EAF1FA', borderRadius: '8px', padding: '16px 18px', marginTop: '8px', marginBottom: '8px' },
  metricsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '14px' },
  metricLabel: { fontSize: '10px', fontWeight: 700, color: 'var(--primary)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '4px' },
  metricValue: { fontSize: '18px', fontWeight: 700, color: 'var(--dark)' },
  metricEmpty: { fontSize: '14px', color: '#8a9ab5', fontStyle: 'italic' },
  suggestLink: { fontSize: '11px', color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline', marginTop: '4px', alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0, fontFamily: 'inherit' },
  rehabToggleBox: { background: 'var(--stripe)', border: '1px solid var(--rule)', borderRadius: '8px', padding: '12px 16px', marginTop: '14px', display: 'flex', alignItems: 'center', gap: '12px' },
};

const FIELD_META = {
  purchasePrice: { defaultLabel: 'Purchase price', type: 'currency' },
  asIsValue: { defaultLabel: 'As-is value', type: 'currency' },
  existingLoanBalance: { defaultLabel: 'Existing loan balance', type: 'currency' },
  downPayment: { defaultLabel: 'Down payment', type: 'currency' },
  rehabBudget: { defaultLabel: 'Rehab budget', type: 'currency' },
  constructionBudget: { defaultLabel: 'Construction budget', type: 'currency' },
  arv: { defaultLabel: 'ARV (after repair value)', type: 'currency' },
  cashOutAmount: { defaultLabel: 'Cash-out amount', type: 'currency' },
  loanAmount: { defaultLabel: 'Loan amount', type: 'currency' },
  currentMonthlyRent: { defaultLabel: 'Current monthly rent', type: 'currency' },
  projectedMonthlyRent: { defaultLabel: 'Projected / market monthly rent', type: 'currency' },
  annualNOI: { defaultLabel: 'Annual NOI', type: 'currency' },
  vacancyRate: { defaultLabel: 'Vacancy rate (%)', type: 'number', placeholder: 'e.g. 5' },
  operatingExpenseRatio: { defaultLabel: 'Operating expense ratio (%)', type: 'number', placeholder: 'e.g. 35' },
};

function formatCurrency(val) {
  const n = String(val).replace(/[^0-9]/g, '');
  if (!n) return '';
  return '$' + parseInt(n, 10).toLocaleString();
}
function parseCurrency(val) { return String(val).replace(/[^0-9]/g, ''); }
function formatPhone(val) {
  const n = val.replace(/[^0-9]/g, '');
  if (n.length <= 3) return n;
  if (n.length <= 6) return n.slice(0, 3) + '-' + n.slice(3);
  return n.slice(0, 3) + '-' + n.slice(3, 6) + '-' + n.slice(6, 10);
}

function Field({ label, required: req, error, children, id }) {
  return (
    <div style={css.fieldGroup} id={id ? `field-${id}` : undefined}>
      <label style={css.label}>{label}{req && <span style={css.required}>*</span>}</label>
      {children}
      {error && <span style={css.errorMsg}>{error}</span>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, error, type = 'text' }) {
  const [focused, setFocused] = useState(false);
  return (
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={css.input(error, focused)} />
  );
}
function PhoneInput({ value, onChange, error }) {
  const [focused, setFocused] = useState(false);
  return (
    <input type="tel" value={value || ''} onChange={e => onChange(formatPhone(e.target.value))}
      placeholder="310-555-0100" onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={css.input(error, focused)} />
  );
}
function CurrencyInput({ value, onChange, placeholder, error }) {
  const [focused, setFocused] = useState(false);
  return (
    <input type="text" value={value ? formatCurrency(value) : ''}
      onChange={e => onChange(parseCurrency(e.target.value))}
      placeholder={placeholder || '$0'}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={css.input(error, focused)} />
  );
}
function SelectInput({ value, onChange, options, placeholder, error }) {
  const [focused, setFocused] = useState(false);
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
      style={css.select(error, focused)}>
      <option value="">{placeholder || 'Select…'}</option>
      {options.map(o => (
        <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
          {typeof o === 'string' ? o : o.label}
        </option>
      ))}
    </select>
  );
}

function Section({ num, title, expanded, onToggle, children }) {
  return (
    <div style={css.sectionCard}>
      <div style={css.sectionHeader(expanded)} onClick={onToggle}>
        <div style={css.sectionNum}>{num}</div>
        <div style={css.sectionTitle}>{title}</div>
        <div style={css.chevron(expanded)}>▼</div>
      </div>
      {expanded && <div style={css.sectionBody}>{children}</div>}
    </div>
  );
}

const EMPTY = {
  loanType: '', loanPurpose: '',
  propertyAddress: '', propertyCity: '', propertyState: '', propertyZip: '',
  propertyType: '', yearBuilt: '', occupancyStatus: '',
  bedrooms: '', bathrooms: '', sqft: '', lotSizeSqft: '', stories: '', floorLevel: '',
  numberOfUnits: '', totalBeds: '', totalBaths: '', occupiedUnits: '',
  commercialSqft: '', residentialSqft: '',
  tenantCount: '', landAcreage: '', landZoning: '', landEntitled: '',
  purchasePrice: '', asIsValue: '', existingLoanBalance: '',
  downPayment: '', rehabBudget: '', constructionBudget: '', arv: '',
  cashOutAmount: '', loanAmount: '',
  currentMonthlyRent: '', projectedMonthlyRent: '', annualNOI: '',
  vacancyRate: '', operatingExpenseRatio: '',
  includesRehab: false,
  rehabFinancing: '',
  targetCloseDate: '', exitStrategy: '',
  requestedRate: '', requestedTerm: '', requestedPoints: '', requestedPrepay: '',
  borrowerFirstName: '', borrowerLastName: '', borrowerEmail: '', borrowerPhone: '',
  borrowerStreetAddress: '', borrowerCity: '', borrowerState: '', borrowerZip: '',
  entityType: '', borrowerEntity: '',
  creditScore: '', borrowerExperience: '', foreignNational: 'No',
  totalLiquidAssets: '', fundsSource: '', fundsInAccountStatus: '', fundsSourceNotes: '',
  bankruptcyHistory: false, bankruptcyType: '', bankruptcyDate: '', bankruptcyStatus: '',
  foreclosureHistory: false, foreclosureDate: '',
  shortSaleDeedInLieu: false, pendingLitigation: false, taxLiensJudgments: false,
  mortgageLates12mo: '', creditEventsNotes: '',
  hasSecondBorrower: false,
  secondBorrowerFirstName: '', secondBorrowerLastName: '',
  secondBorrowerEmail: '', secondBorrowerPhone: '',
  additionalNotes: '', submittedBy: '',
};

function validate(form, spec) {
  const errors = {};
  if (!form.loanType) errors.loanType = 'Select a loan type';
  if (!form.loanPurpose) errors.loanPurpose = 'Select a loan purpose';
  if (!form.propertyAddress) errors.propertyAddress = 'Required';
  if (!form.propertyCity) errors.propertyCity = 'Required';
  if (!form.propertyState) errors.propertyState = 'Required';
  if (!form.propertyZip) errors.propertyZip = 'Required';
  if (!form.propertyType) errors.propertyType = 'Required';
  if (!form.occupancyStatus) errors.occupancyStatus = 'Required';

  getVisiblePropertyFields(form.propertyType).forEach(([key, cfg]) => {
    if (cfg.required?.includes(form.propertyType) && !form[key]) errors[key] = 'Required';
  });

  Object.keys(spec.required).forEach(key => {
    if (!form[key]) errors[key] = 'Required';
  });

  if (isIncomeProducingLoan(form.loanType)) {
    if (!form.currentMonthlyRent && !form.projectedMonthlyRent) {
      errors.currentMonthlyRent = 'Enter current OR projected rent';
    }
  }

  if (!form.borrowerFirstName) errors.borrowerFirstName = 'Required';
  if (!form.borrowerLastName) errors.borrowerLastName = 'Required';
  if (!form.borrowerEmail) errors.borrowerEmail = 'Required';
  if (form.borrowerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.borrowerEmail)) errors.borrowerEmail = 'Enter a valid email';
  if (!form.entityType) errors.entityType = 'Required';
  if (!form.creditScore) errors.creditScore = 'Required';
  if (!form.borrowerExperience) errors.borrowerExperience = 'Required';
  if (!form.totalLiquidAssets) errors.totalLiquidAssets = 'Required — best estimate is fine';

  if (form.bankruptcyHistory) {
    if (!form.bankruptcyType) errors.bankruptcyType = 'Required';
    if (!form.bankruptcyDate) errors.bankruptcyDate = 'Required';
    if (!form.bankruptcyStatus) errors.bankruptcyStatus = 'Required';
  }
  if (form.foreclosureHistory && !form.foreclosureDate) {
    errors.foreclosureDate = 'Required';
  }
  return errors;
}

export default function IntakeForm({ onSubmit }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [expandedSections, setExpandedSections] = useState({ 1: true, 2: true, 3: true, 4: true, 5: true });
  const toggleSection = (n) => setExpandedSections(s => ({ ...s, [n]: !s[n] }));

  const set = useCallback((key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => { const n = { ...e }; delete n[key]; return n; });
  }, []);

  const handleLoanTypeChange = (newLoanType) => {
    setForm(f => {
      const validPurposes = getAvailablePurposes(newLoanType);
      const keepPurpose = validPurposes.includes(f.loanPurpose);
      return { ...f, loanType: newLoanType, loanPurpose: keepPurpose ? f.loanPurpose : '' };
    });
    setErrors(e => { const n = { ...e }; delete n.loanType; return n; });
  };

  const num = (v) => {
    if (!v) return 0;
    const n = parseFloat(String(v).replace(/[^0-9.-]/g, ''));
    return isNaN(n) ? 0 : n;
  };

  const spec = getLoanFieldSpec(form.loanType, form.loanPurpose, form.includesRehab);
  const availablePurposes = getAvailablePurposes(form.loanType);
  const showRehabToggle = showsRehabToggle(form.loanType);

  // ===== METRICS =====
  const purchase = num(form.purchasePrice);
  const asIs = num(form.asIsValue);
  const existingBalance = num(form.existingLoanBalance);
  const down = num(form.downPayment);
  const rehab = num(form.rehabBudget);
  const construction = num(form.constructionBudget);
  const arv = num(form.arv);
  const loan = num(form.loanAmount);

  const lenderFundsRehab = form.rehabFinancing !== 'Borrower self-funds rehab';
  const rehabForCost = lenderFundsRehab ? rehab : 0;

  const baseValue = purchase > 0 ? purchase : asIs;

  const totalCost = (purchase > 0)
    ? purchase + rehabForCost + construction
    : existingBalance + rehabForCost + construction;

  const ltv = baseValue > 0 && loan > 0 ? ((loan / baseValue) * 100).toFixed(1) : '';
  const ltc = totalCost > 0 && loan > 0 ? ((loan / totalCost) * 100).toFixed(1) : '';
  const ltarv = arv > 0 && loan > 0 ? ((loan / arv) * 100).toFixed(1) : '';

  // Estimated profit at ARV = ARV - Purchase Price - Rehab Budget
  // Only meaningful for Fix & Flip deals; shown when ARV, Purchase, and Rehab are all known
  const estProfit = (arv > 0 && purchase > 0 && rehab > 0) ? (arv - purchase - rehab) : 0;
  const showEstProfit = form.loanType === 'fix-flip' && estProfit !== 0;

  const showLTC = totalCost > baseValue && totalCost > 0;
  const showLTARV = arv > 0;
  const showTotalCost = (rehabForCost > 0 || construction > 0);

  const suggestedLoan = purchase > 0
    ? Math.max(0, purchase + rehabForCost + construction - down)
    : Math.max(0, existingBalance + rehabForCost + construction);
  const canSuggestLoan = suggestedLoan > 0;

  const visiblePropertyFields = getVisiblePropertyFields(form.propertyType);

  async function handleSubmit() {
    const errs = validate(form, spec);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setExpandedSections({ 1: true, 2: true, 3: true, 4: true, 5: true });
      const firstErrKey = Object.keys(errs)[0];
      setTimeout(() => {
        const el = document.getElementById(`field-${firstErrKey}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    setLoading(true);
    setSubmitError('');
    try {
      const payload = {
        ...form,
        ltvRequested: ltv ? ltv + '%' : '',
        ltcRequested: showLTC && ltc ? ltc + '%' : '',
        ltarvRequested: showLTARV && ltarv ? ltarv + '%' : '',
        totalProjectCost: showTotalCost ? totalCost : '',
        estProfitAtArv: showEstProfit ? estProfit : '',
      };
      const res = await fetch('/.netlify/functions/submit-deal-airtable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Submission failed. Please try again.');
      onSubmit({ dealId: data.dealId, dealName: data.dealName, form: payload });
    } catch (err) {
      setSubmitError(err.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  }

  function renderSpecField(key) {
    if (!spec.show[key]) return null;
    const meta = FIELD_META[key];
    if (!meta) return null;
    const label = spec.labels[key] || meta.defaultLabel;
    const isRequired = !!spec.required[key];
    const value = form[key];
    const error = errors[key];

    return (
      <Field key={key} label={label} required={isRequired} error={error} id={key}>
        {meta.type === 'currency' ? (
          <CurrencyInput value={value} onChange={v => set(key, v)} placeholder={meta.placeholder} error={error} />
        ) : (
          <TextInput value={value} onChange={v => set(key, v)} placeholder={meta.placeholder} type={meta.type === 'number' ? 'number' : 'text'} error={error} />
        )}
      </Field>
    );
  }

  function renderPropertyField(key, cfg) {
    const value = form[key];
    const error = errors[key];
    const required = isPropertyFieldRequired(key, form.propertyType);
    return (
      <Field key={key} label={cfg.label} required={required} error={error} id={key}>
        {cfg.type === 'select' ? (
          <SelectInput value={value} onChange={v => set(key, v)} options={cfg.options} error={error} />
        ) : (
          <TextInput value={value} onChange={v => set(key, v)} placeholder={cfg.placeholder} type={cfg.type === 'number' ? 'number' : 'text'} error={error} />
        )}
      </Field>
    );
  }

  const LOAN_FIELD_ORDER = [
    'purchasePrice', 'asIsValue', 'existingLoanBalance',
    'downPayment',
    'rehabBudget', 'constructionBudget', 'arv',
    'cashOutAmount',
    'loanAmount',
  ];

  const INCOME_FIELD_ORDER = ['currentMonthlyRent', 'projectedMonthlyRent', 'annualNOI'];
  const SECONDARY_INCOME_ORDER = ['vacancyRate', 'operatingExpenseRatio'];

  const hasIncomeFields = INCOME_FIELD_ORDER.some(k => spec.show[k]);
  const hasSecondaryIncome = SECONDARY_INCOME_ORDER.some(k => spec.show[k]);

  return (
    <div style={css.page}>
      <div style={css.header}>
        <div style={css.headerInner}>
          <div style={css.headerLeft}>
            <div style={css.logoText}>Crafted Capital Co.</div>
            <div style={css.headerSubtitle}>Deal Intake Portal</div>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--accent)', textAlign: 'right' }}>
            <div style={{ fontWeight: 600, color: '#fff', marginBottom: '2px' }}>New Deal Submission</div>
            All fields marked <span style={{ color: '#ff9a9a' }}>*</span> are required
          </div>
        </div>
        <div style={css.accentRule} />
      </div>

      <div style={css.body}>
        {submitError && (
          <div style={css.errorBanner}><strong>Submission error:</strong> {submitError}</div>
        )}

        <Section num="1" title="LOAN TYPE" expanded={expandedSections[1]} onToggle={() => toggleSection(1)}>
          <Field label="Select loan type" required error={errors.loanType} id="loanType">
            <div style={css.loanTypeGrid}>
              {LOAN_TYPES.map(lt => (
                <button key={lt.value} type="button"
                  style={css.loanTypeBtn(form.loanType === lt.value)}
                  onClick={() => handleLoanTypeChange(lt.value)}>
                  <span style={css.loanTypeDot(form.loanType === lt.value)} />
                  {lt.label}
                </button>
              ))}
            </div>
          </Field>
          <div style={{ marginTop: '18px' }}>
            <Field label="Loan purpose" required error={errors.loanPurpose} id="loanPurpose">
              <SelectInput value={form.loanPurpose} onChange={v => set('loanPurpose', v)}
                options={availablePurposes} error={errors.loanPurpose} />
            </Field>
          </div>
        </Section>

        <Section num="2" title="SUBJECT PROPERTY" expanded={expandedSections[2]} onToggle={() => toggleSection(2)}>
          <div style={{ ...css.grid2, marginBottom: '18px' }}>
            <Field label="Property address" required error={errors.propertyAddress} id="propertyAddress">
              <TextInput value={form.propertyAddress} onChange={v => set('propertyAddress', v)} placeholder="123 Main Street" error={errors.propertyAddress} />
            </Field>
            <Field label="City" required error={errors.propertyCity} id="propertyCity">
              <TextInput value={form.propertyCity} onChange={v => set('propertyCity', v)} placeholder="Los Angeles" error={errors.propertyCity} />
            </Field>
          </div>
          <div style={css.grid4}>
            <Field label="State" required error={errors.propertyState} id="propertyState">
              <SelectInput value={form.propertyState} onChange={v => set('propertyState', v)} options={STATES} placeholder="State" error={errors.propertyState} />
            </Field>
            <Field label="ZIP code" required error={errors.propertyZip} id="propertyZip">
              <TextInput value={form.propertyZip} onChange={v => set('propertyZip', v)} placeholder="90210" error={errors.propertyZip} />
            </Field>
            <Field label="Year built" error={errors.yearBuilt}>
              <TextInput value={form.yearBuilt} onChange={v => set('yearBuilt', v)} placeholder="1962" error={errors.yearBuilt} />
            </Field>
            <Field label="Property type" required error={errors.propertyType} id="propertyType">
              <SelectInput value={form.propertyType} onChange={v => set('propertyType', v)} options={PROPERTY_TYPES} error={errors.propertyType} />
            </Field>
          </div>
          <div style={{ marginTop: '18px', maxWidth: '320px' }}>
            <Field label="Occupancy status" required error={errors.occupancyStatus} id="occupancyStatus">
              <SelectInput value={form.occupancyStatus} onChange={v => set('occupancyStatus', v)} options={OCCUPANCY_STATUS} error={errors.occupancyStatus} />
            </Field>
          </div>
          {visiblePropertyFields.length > 0 && (
            <div style={css.subSection}>
              <div style={css.subSectionTitle}>Property details</div>
              <div style={css.subSectionHint}>Fill in what you know — staff will verify before submission.</div>
              <div style={css.grid4}>
                {visiblePropertyFields.map(([key, cfg]) => renderPropertyField(key, cfg))}
              </div>
            </div>
          )}
        </Section>

        <Section num="3" title="LOAN STRUCTURE" expanded={expandedSections[3]} onToggle={() => toggleSection(3)}>

          <div style={css.subSectionTitle}>Deal metrics (auto-calculated)</div>
          <div style={css.subSectionHint}>Updates live as you fill in figures below.</div>
          <div style={css.metricsBox}>
            <div style={css.metricsGrid}>
              <div>
                <div style={css.metricLabel}>LTV</div>
                {ltv ? <div style={css.metricValue}>{ltv}%</div> : <div style={css.metricEmpty}>—</div>}
              </div>
              {showLTC && (
                <div>
                  <div style={css.metricLabel}>LTC</div>
                  {ltc ? <div style={css.metricValue}>{ltc}%</div> : <div style={css.metricEmpty}>—</div>}
                </div>
              )}
              {showLTARV && (
                <div>
                  <div style={css.metricLabel}>LTARV</div>
                  {ltarv ? <div style={css.metricValue}>{ltarv}%</div> : <div style={css.metricEmpty}>—</div>}
                </div>
              )}
              {showTotalCost && (
                <div>
                  <div style={css.metricLabel}>Total project cost</div>
                  {totalCost > 0 ? <div style={css.metricValue}>${totalCost.toLocaleString()}</div> : <div style={css.metricEmpty}>—</div>}
                </div>
              )}
              {showEstProfit && (
                <div>
                  <div style={css.metricLabel}>Est. profit at ARV</div>
                  <div style={css.metricValue}>${estProfit.toLocaleString()}</div>
                </div>
              )}
            </div>
          </div>

          {showRehabToggle && (
            <div style={css.rehabToggleBox}>
              <input type="checkbox" id="includesRehab" checked={form.includesRehab}
                onChange={e => set('includesRehab', e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }} />
              <label htmlFor="includesRehab" style={{ fontSize: '13px', color: 'var(--mid)', fontWeight: 600, cursor: 'pointer' }}>
                This deal includes rehab or improvements
              </label>
            </div>
          )}

          <div style={css.subSection}>
            <div style={css.subSectionTitle}>Deal financials</div>
            <div style={css.subSectionHint}>
              {!form.loanType || !form.loanPurpose
                ? 'Select loan type and purpose above to see relevant fields.'
                : 'Enter all known figures. Metrics above update as you type.'}
            </div>
            <div style={{ ...css.grid4, marginTop: '4px' }}>
              {LOAN_FIELD_ORDER.filter(k => k !== 'loanAmount').map(key => renderSpecField(key))}

              {spec.show.loanAmount && (
                <Field label="Loan amount" required error={errors.loanAmount} id="loanAmount">
                  <CurrencyInput value={form.loanAmount} onChange={v => set('loanAmount', v)} error={errors.loanAmount} />
                  {canSuggestLoan && (
                    <button type="button" style={css.suggestLink}
                      onClick={() => set('loanAmount', String(suggestedLoan))}>
                      Suggest: ${suggestedLoan.toLocaleString()}
                    </button>
                  )}
                </Field>
              )}
            </div>

            {spec.show.rehabBudget && (
              <div style={{ marginTop: '14px', maxWidth: '320px' }}>
                <Field label="Rehab financing" error={errors.rehabFinancing}>
                  <SelectInput value={form.rehabFinancing} onChange={v => set('rehabFinancing', v)} options={REHAB_FINANCING} placeholder="Select…" error={errors.rehabFinancing} />
                </Field>
                <div style={{ fontSize: '11px', color: '#8a9ab5', marginTop: '4px', fontStyle: 'italic' }}>
                  Default: Lender funds rehab. Choose "Borrower self-funds" if you'll pay for rehab out of pocket.
                </div>
              </div>
            )}
          </div>

          {hasIncomeFields && (
            <div style={css.subSection}>
              <div style={css.subSectionTitle}>Income & rents</div>
              <div style={css.subSectionHint}>Enter current rent if leased, or projected/market rent if vacant. At least one is required.</div>
              <div style={css.grid3}>
                {INCOME_FIELD_ORDER.map(key => renderSpecField(key))}
              </div>
              {hasSecondaryIncome && (
                <div style={{ ...css.grid3, marginTop: '14px' }}>
                  {SECONDARY_INCOME_ORDER.map(key => renderSpecField(key))}
                </div>
              )}
            </div>
          )}

          <div style={css.subSection}>
            <div style={css.subSectionTitle}>Loan request</div>
            <div style={css.subSectionHint}>Your target terms — lenders may counter.</div>
            <div style={css.grid4}>
              <Field label="Rate (%)" error={errors.requestedRate}>
                <TextInput value={form.requestedRate} onChange={v => set('requestedRate', v)} type="number" placeholder="e.g. 10.5" error={errors.requestedRate} />
              </Field>
              <Field label="Loan term" error={errors.requestedTerm}>
                <SelectInput value={form.requestedTerm} onChange={v => set('requestedTerm', v)} options={LOAN_TERMS} error={errors.requestedTerm} />
              </Field>
              <Field label="Points" error={errors.requestedPoints}>
                <TextInput value={form.requestedPoints} onChange={v => set('requestedPoints', v)} type="number" placeholder="e.g. 2" error={errors.requestedPoints} />
              </Field>
              <Field label="Prepay penalty" error={errors.requestedPrepay}>
                <SelectInput value={form.requestedPrepay} onChange={v => set('requestedPrepay', v)} options={PPP_OPTIONS} error={errors.requestedPrepay} />
              </Field>
            </div>
          </div>

          <div style={css.subSection}>
            <div style={css.subSectionTitle}>Timeline & exit</div>
            <div style={css.grid3}>
              <Field label="Target close date" error={errors.targetCloseDate}>
                <TextInput value={form.targetCloseDate} onChange={v => set('targetCloseDate', v)} type="date" error={errors.targetCloseDate} />
              </Field>
              <Field label="Exit strategy" error={errors.exitStrategy}>
                <SelectInput value={form.exitStrategy} onChange={v => set('exitStrategy', v)} options={EXIT_STRATEGIES} error={errors.exitStrategy} />
              </Field>
            </div>
          </div>
        </Section>

        <Section num="4" title="BORROWER / SPONSOR" expanded={expandedSections[4]} onToggle={() => toggleSection(4)}>
          <div style={css.subSectionTitle}>Identity</div>
          <div style={{ ...css.grid4, marginTop: '10px' }}>
            <Field label="First name" required error={errors.borrowerFirstName} id="borrowerFirstName">
              <TextInput value={form.borrowerFirstName} onChange={v => set('borrowerFirstName', v)} placeholder="Jane" error={errors.borrowerFirstName} />
            </Field>
            <Field label="Last name" required error={errors.borrowerLastName} id="borrowerLastName">
              <TextInput value={form.borrowerLastName} onChange={v => set('borrowerLastName', v)} placeholder="Smith" error={errors.borrowerLastName} />
            </Field>
            <Field label="Email" required error={errors.borrowerEmail} id="borrowerEmail">
              <TextInput value={form.borrowerEmail} onChange={v => set('borrowerEmail', v)} placeholder="jane@email.com" type="email" error={errors.borrowerEmail} />
            </Field>
            <Field label="Phone" error={errors.borrowerPhone}>
              <PhoneInput value={form.borrowerPhone} onChange={v => set('borrowerPhone', v)} error={errors.borrowerPhone} />
            </Field>
          </div>
          <div style={{ ...css.grid4, marginTop: '18px' }}>
            <Field label="Entity name" error={errors.borrowerEntity}>
              <TextInput value={form.borrowerEntity} onChange={v => set('borrowerEntity', v)} placeholder="Smith Capital LLC" error={errors.borrowerEntity} />
            </Field>
            <Field label="Entity type" required error={errors.entityType} id="entityType">
              <SelectInput value={form.entityType} onChange={v => set('entityType', v)} options={ENTITY_TYPES} error={errors.entityType} />
            </Field>
            <Field label="FICO score" required error={errors.creditScore} id="creditScore">
              <TextInput value={form.creditScore} onChange={v => set('creditScore', v)} type="number" placeholder="720" error={errors.creditScore} />
            </Field>
            <Field label="Completed projects" required error={errors.borrowerExperience} id="borrowerExperience">
              <TextInput value={form.borrowerExperience} onChange={v => set('borrowerExperience', v)} type="number" placeholder="8" error={errors.borrowerExperience} />
            </Field>
          </div>

          <div style={css.subSection}>
            <div style={css.subSectionTitle}>Borrower home address</div>
            <div style={css.subSectionHint}>Where the borrower personally resides — not the subject property. Used for lender packages and identity verification.</div>
            <div style={{ ...css.grid2, marginBottom: '14px' }}>
              <Field label="Street address" error={errors.borrowerStreetAddress}>
                <TextInput value={form.borrowerStreetAddress} onChange={v => set('borrowerStreetAddress', v)} placeholder="456 Oak Avenue" error={errors.borrowerStreetAddress} />
              </Field>
              <Field label="City" error={errors.borrowerCity}>
                <TextInput value={form.borrowerCity} onChange={v => set('borrowerCity', v)} placeholder="Los Angeles" error={errors.borrowerCity} />
              </Field>
            </div>
            <div style={css.grid4}>
              <Field label="State" error={errors.borrowerState}>
                <SelectInput value={form.borrowerState} onChange={v => set('borrowerState', v)} options={STATES} placeholder="State" error={errors.borrowerState} />
              </Field>
              <Field label="ZIP code" error={errors.borrowerZip}>
                <TextInput value={form.borrowerZip} onChange={v => set('borrowerZip', v)} placeholder="90210" error={errors.borrowerZip} />
              </Field>
              <Field label="Foreign national?" error={errors.foreignNational}>
                <SelectInput value={form.foreignNational} onChange={v => set('foreignNational', v)} options={['No', 'Yes']} error={errors.foreignNational} />
              </Field>
            </div>
          </div>

          <div style={css.subSection}>
            <div style={css.subSectionTitle}>Liquidity & source of funds</div>
            <div style={css.subSectionHint}>Total liquid assets and where the money to close is coming from. Source of funds covers down payment, closing costs, and reserves — select the largest source.</div>
            <div style={css.grid3}>
              <Field label="Total liquid assets" required error={errors.totalLiquidAssets} id="totalLiquidAssets">
                <CurrencyInput value={form.totalLiquidAssets} onChange={v => set('totalLiquidAssets', v)} placeholder="$0" error={errors.totalLiquidAssets} />
              </Field>
              <Field label="Source of funds" error={errors.fundsSource}>
                <SelectInput value={form.fundsSource} onChange={v => set('fundsSource', v)} options={FUNDS_SOURCES} error={errors.fundsSource} />
              </Field>
              <Field label="Funds in account?" error={errors.fundsInAccountStatus}>
                <SelectInput value={form.fundsInAccountStatus} onChange={v => set('fundsInAccountStatus', v)} options={FUNDS_STATUS} error={errors.fundsInAccountStatus} />
              </Field>
            </div>
            {form.fundsInAccountStatus && form.fundsInAccountStatus !== 'In account' && (
              <div style={{ marginTop: '14px' }}>
                <Field label="Funds source details" error={errors.fundsSourceNotes}>
                  <textarea value={form.fundsSourceNotes}
                    onChange={e => set('fundsSourceNotes', e.target.value)}
                    placeholder="e.g. Cash-out refinance on rental closing 6/30, sale of brokerage holdings, etc."
                    style={css.textarea(errors.fundsSourceNotes, false)} />
                </Field>
              </div>
            )}
          </div>

          <div style={css.subSection}>
            <div style={css.subSectionTitle}>Credit events (last 7 years)</div>
            <div style={css.subSectionHint}>Lenders ask about these on every deal. Disclose now to avoid mid-process surprises.</div>
            <div style={css.creditChecks}>
              <label style={css.creditCheckBox(form.bankruptcyHistory)}>
                <input type="checkbox" checked={form.bankruptcyHistory} onChange={e => set('bankruptcyHistory', e.target.checked)} style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }} />
                Bankruptcy
              </label>
              <label style={css.creditCheckBox(form.foreclosureHistory)}>
                <input type="checkbox" checked={form.foreclosureHistory} onChange={e => set('foreclosureHistory', e.target.checked)} style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }} />
                Foreclosure
              </label>
              <label style={css.creditCheckBox(form.shortSaleDeedInLieu)}>
                <input type="checkbox" checked={form.shortSaleDeedInLieu} onChange={e => set('shortSaleDeedInLieu', e.target.checked)} style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }} />
                Short sale / deed in lieu
              </label>
              <label style={css.creditCheckBox(form.pendingLitigation)}>
                <input type="checkbox" checked={form.pendingLitigation} onChange={e => set('pendingLitigation', e.target.checked)} style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }} />
                Pending litigation
              </label>
              <label style={css.creditCheckBox(form.taxLiensJudgments)}>
                <input type="checkbox" checked={form.taxLiensJudgments} onChange={e => set('taxLiensJudgments', e.target.checked)} style={{ width: '16px', height: '16px', accentColor: 'var(--primary)' }} />
                Tax liens / judgments
              </label>
              <div style={css.numberBox}>
                <span style={{ color: 'var(--mid)', flex: 1 }}>Mortgage lates 12mo</span>
                <input type="number" min="0" value={form.mortgageLates12mo || ''} onChange={e => set('mortgageLates12mo', e.target.value)} style={css.numberInput} placeholder="0" />
              </div>
            </div>

            {form.bankruptcyHistory && (
              <div style={css.bankruptcyDetail}>
                <div style={css.bankruptcyDetailTitle}>Bankruptcy details</div>
                <div style={css.grid3}>
                  <Field label="Type" required error={errors.bankruptcyType} id="bankruptcyType">
                    <SelectInput value={form.bankruptcyType} onChange={v => set('bankruptcyType', v)} options={BANKRUPTCY_TYPES} error={errors.bankruptcyType} />
                  </Field>
                  <Field label="Date" required error={errors.bankruptcyDate} id="bankruptcyDate">
                    <TextInput value={form.bankruptcyDate} onChange={v => set('bankruptcyDate', v)} type="date" error={errors.bankruptcyDate} />
                  </Field>
                  <Field label="Status" required error={errors.bankruptcyStatus} id="bankruptcyStatus">
                    <SelectInput value={form.bankruptcyStatus} onChange={v => set('bankruptcyStatus', v)} options={BANKRUPTCY_STATUS} error={errors.bankruptcyStatus} />
                  </Field>
                </div>
              </div>
            )}

            {form.foreclosureHistory && (
              <div style={css.bankruptcyDetail}>
                <div style={css.bankruptcyDetailTitle}>Foreclosure details</div>
                <Field label="Date completed" required error={errors.foreclosureDate} id="foreclosureDate">
                  <TextInput value={form.foreclosureDate} onChange={v => set('foreclosureDate', v)} type="date" error={errors.foreclosureDate} />
                </Field>
              </div>
            )}

            <div style={{ marginTop: '14px' }}>
              <Field label="Credit events notes (optional)" error={errors.creditEventsNotes}>
                <textarea value={form.creditEventsNotes}
                  onChange={e => set('creditEventsNotes', e.target.value)}
                  placeholder="Context borrower wants to provide — e.g. bankruptcy was 6 years ago due to medical, discharged clean, no debt since."
                  style={css.textarea(errors.creditEventsNotes, false)} />
              </Field>
            </div>
          </div>

          <label style={css.checkboxRow}>
            <input type="checkbox" checked={form.hasSecondBorrower} onChange={e => set('hasSecondBorrower', e.target.checked)} style={{ width: '16px', height: '16px', accentColor: 'var(--primary)', cursor: 'pointer' }} />
            Add a second borrower / co-borrower
          </label>
          {form.hasSecondBorrower && (
            <div style={css.secondBorrowerBox}>
              <div style={css.secondBorrowerTitle}>Second Borrower / Co-Borrower</div>
              <div style={{ ...css.grid2, marginBottom: '14px' }}>
                <Field label="First name" error={errors.secondBorrowerFirstName}>
                  <TextInput value={form.secondBorrowerFirstName} onChange={v => set('secondBorrowerFirstName', v)} placeholder="John" error={errors.secondBorrowerFirstName} />
                </Field>
                <Field label="Last name" error={errors.secondBorrowerLastName}>
                  <TextInput value={form.secondBorrowerLastName} onChange={v => set('secondBorrowerLastName', v)} placeholder="Smith" error={errors.secondBorrowerLastName} />
                </Field>
              </div>
              <div style={css.grid2}>
                <Field label="Email" error={errors.secondBorrowerEmail}>
                  <TextInput value={form.secondBorrowerEmail} onChange={v => set('secondBorrowerEmail', v)} placeholder="john@email.com" type="email" error={errors.secondBorrowerEmail} />
                </Field>
                <Field label="Phone" error={errors.secondBorrowerPhone}>
                  <PhoneInput value={form.secondBorrowerPhone} onChange={v => set('secondBorrowerPhone', v)} error={errors.secondBorrowerPhone} />
                </Field>
              </div>
            </div>
          )}
        </Section>

        <Section num="5" title="NOTES & SUBMISSION" expanded={expandedSections[5]} onToggle={() => toggleSection(5)}>
          <div style={css.grid2}>
            <Field label="Additional notes" error={errors.additionalNotes}>
              <textarea value={form.additionalNotes} onChange={e => set('additionalNotes', e.target.value)} placeholder="Anything relevant — occupancy, motivation, timeline pressure, title notes…" style={css.textarea(errors.additionalNotes, false)} />
            </Field>
            <Field label="Submitted by" error={errors.submittedBy}>
              <TextInput value={form.submittedBy} onChange={v => set('submittedBy', v)} placeholder="Your initials" error={errors.submittedBy} />
            </Field>
          </div>
        </Section>

        <div style={css.submitArea}>
          <div style={css.submitNote}>
            Submitting this form will create a deal record and associate the borrower contact.
          </div>
          <button type="button" style={css.submitBtn(loading)} onClick={handleSubmit} disabled={loading}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = 'var(--mid)'; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.background = 'var(--primary)'; }}>
            {loading ? (
              <>
                <span style={{ display: 'inline-block', width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                Submitting…
              </>
            ) : (
              <>Submit Deal →</>
            )}
          </button>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}
